import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Form, Space, Input, Button, Flex, Grid, Typography } from 'antd';
import Icon from '@ant-design/icons';
import { useStripe, CardElement, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { useIntercom } from 'react-use-intercom';
import { useTranslation } from 'react-i18next';

import i18n from '@Shared/utils/i18n';
import { CreditCardIcon, PaypalIcon, StripeLogo } from '@Shared/assets/images';
import { useSubmitTrialFormMutation, usePostPaypalMutation } from '@Entities/signup/api/signupSlice';
import { ga4event } from '@Shared/utils/ga4event';
import useLocalStorage from '@Shared/utils/useLocalStorage';
import { PlanType } from '@Features/auth/signin/ui/TrialStep';

import './styles.less';

const ADVERTISER_PAYMENT_METHOD_COOKIE = 'advertiser_payment_method_on_signup';

// If app is running on a localhost it will not work, use signup2.dev.tap domain
function setSignupPaymentMethodCookie(method: 'paypal' | 'credit card') {
    try {
        const rootDomain = window.location.host.split('.').slice(1).join('.');
        const expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString();
        const base = `${ADVERTISER_PAYMENT_METHOD_COOKIE}=${encodeURIComponent(method)}; Expires=${expires}; Path=/`;
        const secureAndSameSite = window.location.protocol === 'https:' ? '; Secure; SameSite=Lax' : '; SameSite=Lax';
        const cookie = `${base}; Domain=.${rootDomain}${secureAndSameSite}`;
        document.cookie = cookie;
    } catch (e) {
        console.error('setSignupPaymentMethodCookie error', e);
    }
}

const { useBreakpoint } = Grid;
const { Paragraph } = Typography;

type TrialFormDataType = {
    nameOnCard: string;
    paymentMethodId?: string;
    paymentProvider: string;
};

export default function TrialForm({
    handleStep,
    activePlan,
    isPlansLoading,
}: {
    handleStep: (arg1: number) => void;
    activePlan: PlanType;
    isPlansLoading: boolean;
}) {
    const { t } = useTranslation('translation', { keyPrefix: 'signup' });
    const storage = useLocalStorage();
    const [submitTrialForm, { isLoading }] = useSubmitTrialFormMutation();
    const [postPaypal, { isLoading: paypalLoading = false }] = usePostPaypalMutation();
    const stripe = useStripe();
    const elements = useElements();
    const { trackEvent } = useIntercom();
    const breakpoints = useBreakpoint();
    const [errors, setErrors] = useState<Partial<TrialFormDataType>>({});
    const [paymentType, setPaymentType] = useState('credit-card');
    const [stripeError, setStripeError] = useState<string | undefined>();
    const [canMakePayment, setCanMakePayment] = useState<boolean>(false);

    // Payment Request для Apple Pay / Google Pay
    const paymentRequest = useMemo(() => {
        if (!stripe) return null;
        return stripe.paymentRequest({
            country: 'US',
            currency: 'usd',
            total: { label: 'Tapfiliate Subscription', amount: 100 },
            requestPayerName: true,
            requestPayerEmail: true,
        });
    }, [stripe]);

    useEffect(() => {
        if (!paymentRequest || !activePlan) return;

        const amountInCents = Math.round(parseFloat(activePlan.price) * 100);

        paymentRequest.update({
            total: {
                label: `Tapfiliate ${activePlan.title} Subscription (trial)`,
                amount: amountInCents,
            },
        });
    }, [paymentRequest, activePlan]);

    useEffect(() => {
        if (!paymentRequest) return;
        paymentRequest.canMakePayment().then((result) => {
            if (result) {
                // Показываем кнопку только если есть Apple Pay или Google Pay
                setCanMakePayment(!!result.applePay || !!result.googlePay); // показать кнопку
            } else {
                setCanMakePayment(false);
            }
        });
    }, [paymentRequest]);

    const resetLocalStorage = useCallback(() => {
        storage.remove('currentStep');
        storage.remove('stepUuid');
        storage.remove('authStepData');
        storage.remove('companyStepData');
        storage.remove('planId');
    }, [storage]);

    const submitTrial = useCallback(
        async (body: TrialFormDataType, planId: unknown) => {
            setStripeError(undefined);
            try {
                const uuid = storage.read('stepUuid');
                const res = await submitTrialForm({
                    body: {
                        language: i18n.language,
                        ...body,
                    },
                    id: uuid,
                    planId,
                });

                if (res?.error && 'data' in res.error) {
                    setErrors((res.error.data as { errors?: Partial<TrialFormDataType> })?.errors || {});
                } else {
                    if (res?.data?.redirectLink) {
                        resetLocalStorage();
                        window.location.href = res.data.redirectLink;
                    }
                }
            } catch (err) {
                console.error('error: ', err);
            }
        },
        [submitTrialForm, storage, resetLocalStorage],
    );

    useEffect(() => {
        ga4event({
            event_name: 'send_advertiser_payment_method_select_payment_method',
            event_option1: 'advertiser registration funnel',
            event_option2: 'onboarding;select_payment_method',
            event_option4: paymentType,
        });
    }, [paymentType]);

    // Обработка Apple Pay / Google Pay
    useEffect(() => {
        if (!paymentRequest) return undefined;
        const handlePaymentMethod = async (ev: PaymentRequestPaymentMethodEvent) => {
            try {
                const formData: TrialFormDataType = {
                    paymentProvider: 'credit-card',
                    nameOnCard: ev.payerName || '',
                    paymentMethodId: ev.paymentMethod.id,
                };

                await submitTrial(formData, activePlan.id);
                ev.complete('success');
            } catch (error) {
                ev.complete('fail');
                setStripeError(error instanceof Error ? error.message : 'Payment failed');
            }
        };
        paymentRequest.on('paymentmethod', handlePaymentMethod);
        return () => {
            paymentRequest.off('paymentmethod', handlePaymentMethod);
        };
    }, [paymentRequest, submitTrial, activePlan]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = e.target?.id?.replace('trial-form_', '');
        ga4event({
            event_name: `enter_${fieldName}`,
            event_option1: 'advertiser registration funnel',
            event_option2: 'onboarding;filled credit card',
        });
    };

    const handleCardElementChange = (event: StripeCardElementChangeEvent) => {
        if (event?.error?.code) {
            setStripeError(t(`form.stripe_error.${event?.error?.code}`));
        } else {
            setStripeError(undefined);
        }
    };

    const handleSubmit = async (formData: TrialFormDataType) => {
        let modifiedFormData = formData;
        if (paymentType === 'credit-card') {
            const cardElement = elements?.getElement(CardElement);
            if (!cardElement) {
                return;
            }
            const stripeRes = await stripe?.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });
            if (stripeRes?.error) {
                ga4event({
                    event_name: 'send_advertiser_payment_method_error_f',
                    event_option1: 'advertiser registration funnel',
                    event_option2: 'onboarding;filled credit card',
                    event_option4: JSON.stringify(stripeRes.error),
                });
                trackEvent('lead-from-signup-step-4', {
                    eventMessage: 'payment step error',
                    formData,
                });

                setStripeError(stripeRes?.error.message);
                return;
            }
            modifiedFormData = {
                ...formData,
                paymentMethodId: stripeRes?.paymentMethod.id,
                paymentProvider: 'credit-card',
            };
            setSignupPaymentMethodCookie('credit card');
            trackEvent('lead-from-signup-step-4', {
                eventMessage: 'payment step success',
                formData,
            });

            submitTrial(modifiedFormData, activePlan.id);
        } else {
            try {
                const uuid = storage.read('stepUuid');
                const body = {
                    language: i18n.language,
                };
                const res = await postPaypal({ body, id: uuid, planId: activePlan.id });

                if (res?.error && 'data' in res.error) {
                    const errors = (res.error.data as { errors?: Partial<TrialFormDataType> })?.errors || {};
                    ga4event({
                        event_name: 'send_advertiser_payment_method_error_f',
                        event_option1: 'advertiser registration funnel',
                        event_option2: 'onboarding;filled paypall',
                    });

                    setErrors(errors);
                } else {
                    setSignupPaymentMethodCookie('paypal');

                    storage.write('currentStep', 'TrialStep');
                    storage.write('planId', activePlan.id);
                    resetLocalStorage();
                    window.location.href = res.data.redirectUrl;
                }
            } catch (err) {
                console.error('error: ', err);
            }
        }
    };

    const actionButtons = (
        <Flex
            justify="space-between"
            style={{ flexDirection: breakpoints.lg ? 'row' : 'column-reverse', gap: 10, marginTop: 6 }}
        >
            <Button
                style={{
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    minWidth: breakpoints.lg ? 100 : '100%',
                    width: 'fit-content',
                }}
                size="large"
                onClick={() => handleStep(-1)}
            >
                {t('form.back')}
            </Button>
            <Button
                type="primary"
                htmlType="submit"
                style={{ textTransform: 'uppercase', fontWeight: 600 }}
                size="large"
                disabled={Boolean(paymentType === 'credit-card' && stripeError) || isPlansLoading}
                loading={isLoading || paypalLoading}
            >
                {t('trial_form.subscribe_start')}
            </Button>
        </Flex>
    );

    return (
        <>
            <Space
                direction="horizontal"
                style={{ marginBottom: 20 }}
            >
                <Button
                    size="large"
                    onClick={() => setPaymentType('credit-card')}
                    className={paymentType !== 'credit-card' ? 'payment-type__button-inactive' : ''}
                    icon={
                        <Icon
                            component={() => (
                                <img
                                    src={CreditCardIcon}
                                    alt="credit-card"
                                />
                            )}
                        />
                    }
                >
                    {t('trial_form.credit_card')}
                </Button>
                <Button
                    size="large"
                    onClick={() => setPaymentType('paypal')}
                    className={paymentType !== 'paypal' ? 'payment-type__button-inactive' : ''}
                    icon={
                        <Icon
                            component={() => (
                                <img
                                    src={PaypalIcon}
                                    alt="paypal"
                                />
                            )}
                        />
                    }
                >
                    {t('trial_form.paypal')}
                </Button>
            </Space>
            {paymentType === 'credit-card' ? (
                <Form
                    name="trial-form"
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark={false}
                >
                    {canMakePayment && paymentRequest && (
                        <>
                            <Form.Item>
                                <PaymentRequestButtonElement
                                    options={{
                                        paymentRequest,
                                        style: {
                                            paymentRequestButton: {
                                                theme: 'dark',
                                                height: '48px',
                                            },
                                        },
                                    }}
                                />
                            </Form.Item>
                            <div style={{ textAlign: 'center', margin: '10px 0', color: '#999' }}>
                                {t('trial_form.or', { defaultValue: 'or' })}
                            </div>
                        </>
                    )}
                    <Form.Item
                        label={t('trial_form.name_card')}
                        name="nameOnCard"
                        rules={[{ required: true, message: t('form.error.enter_name_on_card') }]}
                        validateStatus={errors?.nameOnCard ? 'error' : ''}
                        help={errors?.nameOnCard || null}
                    >
                        <Input
                            size="large"
                            onBlur={handleChange}
                            className="signup-input"
                        />
                    </Form.Item>
                    <Form.Item
                        label={t('trial_form.card_number')}
                        validateStatus={errors?.paymentProvider ? 'error' : ''}
                        help={errors?.paymentProvider || null}
                        extra={
                            <>
                                {t('trial_form.process_partner')}{' '}
                                <Icon
                                    style={{ marginLeft: '2px', verticalAlign: 'bottom' }}
                                    component={() => (
                                        <img
                                            src={StripeLogo}
                                            alt="Stripe"
                                        />
                                    )}
                                />
                            </>
                        }
                    >
                        <div className="stripe-card-element">
                            <CardElement
                                onChange={handleCardElementChange}
                                options={{
                                    style: {
                                        base: {
                                            fontSize: '16px',
                                        },
                                    },
                                }}
                            />
                        </div>
                        {stripeError ? (
                            <div
                                id="trial-form_stripe_help"
                                className="ant-form-item-explain ant-form-css-var ant-form-item-explain-connected"
                                role="alert"
                            >
                                <div className="ant-form-item-explain-error">{stripeError}</div>
                            </div>
                        ) : null}
                    </Form.Item>
                    <Form.Item style={{ margin: 0 }}>{actionButtons}</Form.Item>
                </Form>
            ) : (
                <>
                    <Paragraph>{t('trial_form.paypal_text')}</Paragraph>
                    <Form
                        name="paypal-form"
                        layout="vertical"
                        onFinish={handleSubmit}
                        style={{ marginTop: 'auto' }}
                        requiredMark={false}
                    >
                        {actionButtons}
                    </Form>
                </>
            )}
        </>
    );
}
